import { onAuthStateChanged, type User } from 'firebase/auth';
import type { Unsubscribe } from 'firebase/database';

import type { RTCIceServerConfig } from '@remote/shared';

import { watchDevices, type DeviceEntry } from './devices.js';
import { initFirebase, signInWithGoogle, signOutOfGoogle } from './firebase.js';
import { isFullscreen, onFullscreenChange, toggleFullscreen } from './fullscreen.js';
import { loadIceServers } from './ice-config.js';
import { createInputCapture, type InputCapture } from './input-capture.js';
import { startSession, type ClientSession } from './session.js';
import { startStatsLoop } from './stats.js';
import { createUi } from './ui.js';

import './styles.css';

/** Backoff exponencial para la reconexión: 1s, 2s, 4s… hasta un tope de 30s. */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

async function bootstrap(): Promise<void> {
  const ui = createUi();
  const { auth, db } = await initFirebase();
  const iceServers: RTCIceServerConfig[] = await loadIceServers();

  let devices: Unsubscribe | null = null;
  let session: ClientSession | null = null;
  let stopStats: (() => void) | null = null;
  let inputCapture: InputCapture | null = null;
  let controlActive = false;

  // Estado de reconexión. `target` es la conexión que el usuario quiere viva; se
  // pone a null solo cuando cuelga o cierra sesión, y eso es lo que distingue una
  // caída (hay que reconectar) de un cierre deliberado (no hay que).
  let target: { device: DeviceEntry; clientUid: string } | null = null;
  let reconnectAttempts = 0;
  let reconnectTimer: number | null = null;

  const clearReconnectTimer = (): void => {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const teardownSession = async (): Promise<void> => {
    const current = session;
    session = null;
    stopStats?.();
    stopStats = null;
    inputCapture?.release();
    inputCapture = null;
    ui.setControlEnabled(false);
    ui.setControlActive(false);
    if (current !== null) await current.close();
  };

  /** Cierre deliberado: cancela la reconexión y limpia la UI por completo. */
  const hangUp = async (): Promise<void> => {
    target = null;
    reconnectAttempts = 0;
    clearReconnectTimer();
    await teardownSession();
    ui.setVideoStream(null);
    ui.showSession(false);
    ui.setConnection('idle');
    ui.setRtt(null);
  };

  const scheduleReconnect = (): void => {
    if (target === null || reconnectTimer !== null) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempts, RECONNECT_MAX_MS);
    reconnectAttempts += 1;
    ui.log(`reconectando en ${(delay / 1000).toFixed(0)} s (intento ${String(reconnectAttempts)})…`);
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      void openSession();
    }, delay);
  };

  const openSession = async (): Promise<void> => {
    if (target === null) return;
    const { device, clientUid } = target;

    // Cierra cualquier sesión previa antes de abrir la nueva, sin tocar `target`
    // (que es lo que mantiene viva la intención de estar conectado).
    await teardownSession();

    ui.showSession(true);
    ui.setConnection('new');
    ui.log(`conectando con "${device.record.name}"…`);

    try {
      const newSession = await startSession({
        db,
        deviceId: device.deviceId,
        clientUid,
        iceServers,
        log: ui.log,
        onConnectionState: (state) => {
          ui.setConnection(state);
          if (state === 'connected') {
            // Conexión buena: se reinicia el contador para que la próxima caída
            // vuelva a empezar el backoff desde 1s.
            reconnectAttempts = 0;
          }
          // 'failed' es terminal para esta PC; 'disconnected' puede recuperarse
          // solo, pero si no lo hace acabará en 'failed'. Se reacciona a 'failed'.
          if (state === 'failed' && target !== null) {
            ui.log('la conexión se ha caído');
            void teardownSession().then(() => scheduleReconnect());
          }
        },
        onRtt: (rtt) => {
          ui.setRtt(rtt);
          ui.log(`pong recibido — round-trip ${rtt.toFixed(1)} ms`);
        },
        onVideoStream: (stream) => {
          ui.setVideoStream(stream);
          ui.setControlEnabled(true);
        },
        onScreens: (screens, activeId) => {
          ui.setScreens(screens, activeId);
        },
      });

      session = newSession;

      inputCapture = createInputCapture({
        video: ui.getVideoElement(),
        videoWrap: ui.getVideoWrap(),
        keyboardInput: ui.getKeyboardInput(),
        send: (message) => newSession.sendInput(message),
        log: ui.log,
        onActiveChange: (active) => {
          controlActive = active;
          ui.setControlActive(active);
          // engage()/release() tocan la pantalla completa; el overlay se sincroniza
          // tras el cambio (el pseudo-fullscreen de iOS no dispara fullscreenchange).
          ui.setOverlayVisible(isFullscreen({ element: ui.getVideoElement(), container: ui.getVideoWrap() }));
        },
      });
      ui.setTouchMode(inputCapture.touchMode);

      stopStats = startStatsLoop(newSession, (stats) => ui.setVideoStats(stats));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      ui.log(`no se pudo abrir la sesión — ${message}`);
      ui.setConnection('failed');
      // Un fallo al montar la sesión también dispara el backoff.
      if (target !== null) scheduleReconnect();
    }
  };

  const connect = (device: DeviceEntry, clientUid: string): void => {
    target = { device, clientUid };
    reconnectAttempts = 0;
    clearReconnectTimer();
    void openSession();
  };

  onAuthStateChanged(auth, (user: User | null) => {
    devices?.();
    devices = null;
    void hangUp();

    if (user === null) {
      ui.setSignedOut();
      return;
    }

    ui.setSignedIn(user.email ?? user.uid);
    ui.log(`sesión iniciada como ${user.email ?? user.uid}`);

    devices = watchDevices(db, user.uid, (list) => {
      ui.renderDevices(list, (device) => connect(device, user.uid));
    });
  });

  ui.onSignIn(() => {
    void signInWithGoogle(auth).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      ui.setError(message);
      ui.log(`fallo al iniciar sesión — ${message}`);
    });
  });

  ui.onSignOut(() => {
    void (async () => {
      await hangUp();
      await signOutOfGoogle(auth);
    })();
  });

  ui.onPing(() => session?.ping());
  ui.onHangUp(() => void hangUp());

  // Un solo botón que alterna: si el control está activo lo suelta, si no lo toma.
  ui.onToggleControl(() => {
    if (inputCapture === null) return;
    if (controlActive) inputCapture.release();
    else void inputCapture.engage();
  });

  ui.onKeyboard(() => ui.focusKeyboard());

  const fsTarget = (): { element: HTMLElement; container: HTMLElement } => ({
    element: ui.getVideoElement(),
    container: ui.getVideoWrap(),
  });

  // Pantalla completa solo-ver: independiente de tomar el control. Usa la API real
  // en Android/desktop y el fallback CSS en iOS (donde requestFullscreen no existe
  // para elementos que no sean el reproductor de vídeo nativo).
  ui.onFullscreen(() => void toggleFullscreen(fsTarget()));

  // La barra superpuesta se muestra siempre que el vídeo esté a pantalla completa,
  // para poder salir y (en táctil) abrir el teclado sin los controles de la página.
  const syncOverlay = (): void => ui.setOverlayVisible(isFullscreen(fsTarget()));
  onFullscreenChange(syncOverlay);
  // El pseudo-fullscreen (iOS) no dispara fullscreenchange, así que también se
  // sincroniza tras las acciones que lo tocan.
  ui.onFullscreen(() => window.setTimeout(syncOverlay, 0));

  ui.onOverlayExit(() => {
    // Salir del control si estaba activo; si no, solo salir de pantalla completa.
    if (controlActive) inputCapture?.release();
    else void toggleFullscreen(fsTarget());
    window.setTimeout(syncOverlay, 0);
  });
  ui.onOverlayKeyboard(() => ui.focusKeyboard());

  ui.onScreenPick((screenId) => session?.selectScreen(screenId));

  ui.setConnection('idle');
  ui.setRtt(null);
  ui.showSession(false);
}

void bootstrap().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  document.body.textContent = `No se pudo arrancar: ${message}`;
});
